import CallReceivedIcon from '@mui/icons-material/CallReceived';
import { useRecoilValue } from 'recoil';

import { getDateRangeText, getFormattedTimeOffsetUTC, getTimezoneOffset } from 'common/util/time';
import * as K from 'common/constants';
import ELEMENT from 'common/element';

import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';
import ShadedText from 'components/common/ShadedText/ShadedText';

import './TimelineLegend.scss';

type Timerange = {
  startTime: number,
  endTime: number,
};

type TimelineLegendProps = {
  timeRange?:     Timerange;
  timeRangeText?: string;
  title:          string;
  subtitle?:      string;
  facet?:         string;
  prependDate?:   boolean;
  includeDay?:    boolean;
  buttonText?:    string;
  buttonLink?:    string;
  buttonTestId?:  ELEMENT;
  value?:         string;
};

/**
 * This component provides an informational header for a graph that describes the graph, provides navigation to
 * an alternate form of the data, and possibly shows details about the point being hovered over.
 *
 * @prop timeRange       time range to show the time range string for
 * @prop timeRangeText   if supplied, overrides the time range property
 * @prop title           description of the graph
 * @prop subtitle        parenthetical addition to title
 * @prop facet           facet being graphed
 * @prop prependDate     if true, prepend date when computing time range text
 * @prop includeDay      if true, include day of week in date
 * @prop buttonText      text for navigation button
 * @prop buttonLink      target link for navigation button
 * @prop buttonTestId    target link for navigation button
 * @prop value           optional value to show next to the time range string
 */
function TimelineLegend({
  timeRange,
  timeRangeText,
  title,
  subtitle,
  facet,
  prependDate,
  includeDay,
  buttonText,
  buttonLink,
  buttonTestId,
  value,
}: TimelineLegendProps): JSX.Element {
  const timezone = useRecoilValue(timezoneState);
  const timezoneOffset = getFormattedTimeOffsetUTC(getTimezoneOffset(timezone));

  const leftItems = [
    <span className="TimelineLegend__Title" data-test-id={ELEMENT.timelineLegendTitle}>{title}</span>,
    facet && facet !== K.RATE.op ? <ShadedText text={facet} /> : null,
    subtitle ? <span className="TimelineLegend__Subtitle" data-test-id={ELEMENT.timelineLegendSubtitle}>{subtitle}</span> : null,
  ];

  if (buttonText) {
    const arrowIcon = <CallReceivedIcon fontSize="small" className="TimelineLegend__ButtonIcon Button__RightIcon" />;
    leftItems.push(
      <Button
        href={buttonLink}
        variant={ButtonVariant.BORDERLESS}
        size="sm"
        rightIcon={arrowIcon}
        testId={buttonTestId}
      >
        {buttonText}
      </Button>,
    );
  }

  let rightItems: Array<JSX.Element> = [];
  if (timeRange) {
    const textToDisplay = timeRangeText
      ?? getDateRangeText(timeRange.startTime, timeRange.endTime, timezone, prependDate, includeDay);
    rightItems = [
      <span className="TimelineLegend__TimeRange">{textToDisplay ?? ''}</span>,
      <span className="TimelineLegend__TimeZone">
        [
        {timezoneOffset}
        ]
      </span>,
    ];
    if (value != null) {
      rightItems.push(<span>-</span>);
      rightItems.push(<span className="TimelineLegend__Events" data-test-id={ELEMENT.timelineLegendNumEvents}>{value}</span>);
    }
  }

  return (
    <Toolbar leftItems={leftItems} rightItems={rightItems} className="TimelineLegend" testId={ELEMENT.timelineLegend} />
  );
}

export default TimelineLegend;
